import { Editor } from '@tiptap/react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FileManager from 'client/components/FileManager/FileManager'
import { mimeTypes } from 'client/constants/editorSettings'
import { LinkAttributes } from '../../enums/link-attributes-enum'
import { LinkSourceTypeEnum } from '../../enums/link-source-type-enum'
import { useToolbarCommands } from '../../hooks/use-toolbar-commands'
import { LinkType } from '../../type/link-type'
import { ToolbarButtonUi } from '../../ui/toolbar-button-ui'
import { detectLinkSource } from '../../utils/detect-link-source'
import { LinkSourceBlog } from './link-source-blog'
import { LinkSourceInput } from './link-source-input'
import { LinkSourcePopup } from './link-source-popup'
import { ToolbarButtonHaveChangesUi } from './ui/toolbar-button-have-changes-ui'
import { ToolbarConfirmButtonUi } from './ui/toolbar-confirm-button-ui'
import { ToolbarInputGroupUi } from './ui/toolbar-input-group-ui'
import { ToolbarLinkUi } from './ui/toolbar-link-ui'

interface LinkToolbarProps {
  editor: Editor
  close: () => void
  isBlogPage: boolean
}

export const LinkToolbar = ({
  editor,
  close,
  isBlogPage,
}: LinkToolbarProps) => {
  const { onSetLink, onUnsetLink } = useToolbarCommands(editor)
  const { t } = useTranslation()
  const [linkState, setLinkState] = useState<LinkType>(
    editor.getAttributes('link'),
  )
  const [showFileManager, setShowFileManager] = useState(false)
  const [linkSource, setLinkSource] = useState(detectLinkSource(linkState))

  const updateURL = (url: string) => {
    setLinkState({
      rel: linkState.rel,
      target: linkState.target,
      href: url,
    })
  }

  const updatePopupId = (popupid: string) => {
    setLinkState({
      popupid,
      rel: linkState.rel,
      target: linkState.target,
    })
  }

  const save = () => {
    const isHasLink =
      linkSource === LinkSourceTypeEnum.Popup
        ? !!linkState.popupid
        : !!linkState.href
    if (isHasLink) {
      onSetLink(linkState)
    } else {
      onUnsetLink()
    }
    setTimeout(close, 500)
  }

  const isLinkReadyForSave = useMemo(() => {
    const linkAttrs = editor.getAttributes('link') || {}
    const stateAttrs = linkState || {}

    const currentHref = linkAttrs.href || ''
    const currentPopupId = linkAttrs.popupid || ''
    const stateHref = stateAttrs.href || ''
    const statePopupId = stateAttrs.popupid || ''

    const isPopup = linkSource === LinkSourceTypeEnum.Popup
    return (
      (isPopup ? currentPopupId : currentHref) !==
      (isPopup ? statePopupId : stateHref)
    )
  }, [editor, linkState, linkSource])
  const toggleMakeOpenInNew = () => {
    const newLink: LinkType = {
      ...linkState,
      target:
        linkState.target === LinkAttributes.targetBlank
          ? ''
          : LinkAttributes.targetBlank,
    }
    onSetLink(newLink)
    setLinkState(newLink)
  }

  const toggleNoFollow = () => {
    const newLink: LinkType = {
      ...linkState,
      rel:
        linkState.rel === LinkAttributes.relNoFollow
          ? ''
          : LinkAttributes.relNoFollow,
    }
    onSetLink(newLink)
    setLinkState(newLink)
  }
  useEffect(() => {
    const changeLinkState = () => {
      setLinkState(editor.getAttributes('link'))
      setLinkSource(detectLinkSource(editor.getAttributes('link')))
    }
    editor.on('selectionUpdate', changeLinkState)
    return () => {
      editor.off('selectionUpdate', changeLinkState)
    }
  }, [])

  const remove = () => {
    onUnsetLink()
    close()
  }

  const togglePopupLinkSource = () => {
    setLinkSource(linkSource =>
      linkSource === LinkSourceTypeEnum.Popup
        ? LinkSourceTypeEnum.Input
        : LinkSourceTypeEnum.Popup,
    )
  }

  const toggleBlogLinkSource = () => {
    if (linkSource === LinkSourceTypeEnum.Blog) {
      setLinkSource(LinkSourceTypeEnum.Input)
    } else {
      setLinkSource(LinkSourceTypeEnum.Blog)
    }
  }

  const toggleShowFileManager = () => {
    setShowFileManager(!showFileManager)
  }

  const afterUpload = ({ path }: { path: string }) => {
    setLinkState({
      href: path,
    })
  }

  return (
    <ToolbarLinkUi>
      <ToolbarInputGroupUi>
        {linkSource === LinkSourceTypeEnum.Input && (
          <LinkSourceInput url={linkState?.href} update={updateURL} />
        )}
        {linkSource === LinkSourceTypeEnum.Popup && (
          <LinkSourcePopup popupid={linkState.popupid} update={updatePopupId} />
        )}
        {linkSource === LinkSourceTypeEnum.Blog && (
          <LinkSourceBlog url={linkState?.href} update={updateURL} />
        )}
        <ToolbarConfirmButtonUi
          onClick={save}
          title={t('core.text_editor.link_menu.confirm')}
          disabled={!isLinkReadyForSave}
        >
          <span className="fas fa-save" />
          {isLinkReadyForSave && <ToolbarButtonHaveChangesUi />}
        </ToolbarConfirmButtonUi>
      </ToolbarInputGroupUi>
      <ToolbarButtonUi
        onClick={toggleShowFileManager}
        title={t('components.core.text_editor.link_menu.use_file_as_link')}
      >
        <span className="fa fa-download" />
      </ToolbarButtonUi>
      {showFileManager && (
        <FileManager
          fileType={null}
          closeFileManager={toggleShowFileManager}
          mimeTypes={mimeTypes.all}
          onInsert={afterUpload}
        />
      )}
      {isBlogPage && (
        <ToolbarButtonUi
          onClick={toggleBlogLinkSource}
          active={linkSource === LinkSourceTypeEnum.Blog}
          title={t('components.core.text_editor.link_menu.blog_link')}
          style={{ position: 'relative' }}
        >
          <span className="far fa-browser" style={{ fontSize: 20 }} />
          <span
            className="far fa-link"
            style={{ position: 'absolute', left: 13, top: 11, fontSize: 9 }}
          />
        </ToolbarButtonUi>
      )}

      <ToolbarButtonUi
        onClick={togglePopupLinkSource}
        active={linkSource === LinkSourceTypeEnum.Popup}
        title={
          linkSource === LinkSourceTypeEnum.Popup
            ? t('components.core.text_editor.link_menu.open_link')
            : t('components.core.text_editor.link_menu.open_popup')
        }
      >
        <span className="far fa-window-restore" />
      </ToolbarButtonUi>
      <ToolbarButtonUi
        onClick={toggleMakeOpenInNew}
        active={linkState.target === LinkAttributes.targetBlank}
        title={t('components.core.text_editor.link_menu.open_in_new_window')}
      >
        <span className="fas fa-external-link-alt" />
      </ToolbarButtonUi>
      <span style={{ color: '#fff', borderRight: '1xp solid #fff' }} />
      <ToolbarButtonUi
        onClick={toggleNoFollow}
        active={linkState.rel === LinkAttributes.relNoFollow}
        title={t('components.core.text_editor.link_menu.no_follow')}
      >
        <span className="fa fa-ban" />
      </ToolbarButtonUi>
      <ToolbarButtonUi
        onClick={remove}
        title={t('components.core.text_editor.link_menu.unlink')}
      >
        <span className="fas fa-unlink" />
      </ToolbarButtonUi>
      <ToolbarButtonUi
        onClick={close}
        marginLeft="auto"
        title={t('components.core.text_editor.link_menu.close_menu')}
        closeButton
      >
        <span className="fa fa-times" />
      </ToolbarButtonUi>
    </ToolbarLinkUi>
  )
}
